<template>
  <el-drawer
    :title="title"
    :visible.sync="showDrawer"
    :with-header="true"
    :before-close="handleClose"
    :size="drawerWidth"
    :withHeader="withHeader"
  >
    <div v-if="isLoading" class="min-h-screen flex items-center justify-center">
      <spinner />
    </div>
    <section v-else class="h-screen overflow-scroll py-4 -mt-3">
      <!-- header -->
      <div
        class="py-5 px-8 flex items-center justify-between transition-all duration-300 ease-linear"
        :class="
          isStatusHistory
            ? 'h-20 w-full sticky top-0 z-20 text-default pt-5 pb-16 bg-muted'
            : isQuestionaireDetails
            ? 'mb-2 border-b'
            : 'border-b mb-10'
        "
      >
        <div
          v-if="isSecondPage || isDepositPage"
          @click="changePage('first-page')"
          class="font text-sm border px-1.5 lg:px-3 py-1.5 rounded-lg font-light flex items-center gap-x-1 lg:gap-x-2.5 cursor-pointer"
        >
          <span>&larr;</span>
          <span class="text-xs lg:text-base">Back</span>
        </div>
        <div
          v-if="isQuestionaireDetails"
          @click="changePage('first-page')"
          class="cursor-pointer"
        >
          <arrow-left />
        </div>
        <h3
          class="font-semibold test-sm"
          :class="{ '-ml-6': isSecondPage || isDepositPage }"
        >
          {{ title }}
        </h3>
        <close-icon @click.native="handleClose" size="h-5 w-5 cursor-pointer" />
      </div>
      <div v-if="isStatusHistory">
        <div class="bg-muted h-10">
          <send-fund-icon
            class="flex justify-center h-22 w-full relative -mb-10 -top-0 items-center"
          />
        </div>
      </div>
      <transition name="fade-in">
        <component
          class="px-4"
          :is="activeComponent"
          :transactionDetails="transactionDetails"
          :rateIncreased="rateIncreased"
          :activeDocumentDetails="activeDocumentDetails"
          @changePage="changePage"
          @close="handleClose"
          @handleTransfer="handleTransfer"
        />
      </transition>
    </section>
  </el-drawer>
</template>
<script>
import breakpoints from '@/utils/breakpoints.js'
import notification from '@/mixins/notification.js'
import app from '@/mixins/app.js'

export default {
  mixins: [notification, app],
  name: 'Drawer',
  data () {
    return {
      breakpoints,
      isLoading: false,
      showDepositDetails: false,
      title: '',
      activeComponent: '',
      withHeader: false,
      activeDocumentDetails: {}
    }
  },
  props: {
    showDrawer: {
      type: Boolean,
      required: true
    },
    transactionDetails: {
      type: Object,
      default: () => {},
      required: true
    },
    rateIncreased: {
      type: Boolean,
      default: () => false,
      required: true
    },
    statusType: {
      type: String,
      default: () => '',
      required: true
    }
  },
  components: {
    Spinner: () => import('@/components/v2/app/Spinner.vue'),
    FirstPage: () =>
      import(
        '@/components/v2/terminal/drawers/transactionDrawer/ongoingTransaction/FirstPage.vue'
      ),
    SecondPage: () =>
      import(
        '@/components/v2/terminal/drawers/transactionDrawer/ongoingTransaction/SecondPage.vue'
      ),
    HistoryPage: () =>
      import(
        '@/components/v2/terminal/drawers/transactionDrawer/HistoryPage.vue'
      ),
    DepositDetails: () =>
      import(
        '@/components/v2/terminal/drawers/transactionDrawer/ongoingTransaction/DepositDetails.vue'
      ),
    QuestionaireForm: () =>
      import(
        '@/components/v2/terminal/drawers/transactionDrawer/QuestionaireForm.vue'
      ),
    QuestionairePage: () =>
      import(
        '@/components/v2/terminal/drawers/transactionDrawer/QuestionairePage.vue'
      ),
    CloseIcon: () => import('@/assets/v2/icons/CloseIcon.vue'),
    Transition: () =>
      import('@/components/v2/animation/transition/Transition.vue'),
    SendFundIcon: () => import('@/assets/v2/icons/SendFundIcon.vue'),
    ArrowLeft: () => import('@/assets/v2/icons/ArrowLeft.vue')
  },
  computed: {
    transferId () {
      return this.$route.params.id
    },
    drawerWidth () {
      return breakpoints.lgAndUp ? '33%' : breakpoints.mdAndUp ? '70%' : '100%'
    },
    isSecondPage () {
      return this.activeComponent === 'second-page'
    },
    isDepositPage () {
      return this.activeComponent === 'deposit-details'
    },
    isQuestionaireForm () {
      return this.activeComponent === 'questionaire-form'
    },
    isQuestionairePage () {
      return this.activeComponent === 'questionaire-page'
    },
    isStatusHistory () {
      return this.statusType === this.$STATUS.HISTORY
    },
    isQuestionaireDetails () {
      return this.isQuestionaireForm || this.isQuestionairePage
    },
    isOngoingStatus () {
      return (
        this.statusType === this.$STATUS.PENDING ||
        this.statusType === this.$STATUS.ONGOING
      )
    }
  },
  created () {
    if (this.isOngoingStatus) {
      this.title = 'Ongoing Transfer'
    } else if (this.isStatusHistory) {
      this.title = 'Transfer Details'
    }
  },
  methods: {
    handleClose () {
      this.$emit('closeDrawer')
    },
    handleTransfer (actionType) {
      this.$emit('handleTransfer', actionType)
    },
    changePage (page, documentDetails) {
      this.activeDocumentDetails = documentDetails
      this.activeComponent = page
      if (page === 'second-page') {
        this.title = 'Recipient Details'
      } else if (page === 'deposit-details') {
        this.title = 'Deposit Details'
      } else if (page === 'questionaire-form' || page === 'questionaire-page') {
        this.title = 'Questionnaire'
      } else {
        this.title = 'Ongoing Transfer'
      }
    },
    async getTransactionDetails () {
      this.isLoading = true
      const payload = {
        transactionType: this.transactionDetails.type
          ? this.transactionDetails.type
          : this.notificationType,
        transactionId: this.transactionDetails.id
          ? this.transactionDetails.id
          : this.notificationID
      }
      const vm = this
      await this.$store
        .dispatch('transactions/getTransactionById', payload)
        .then(() => {
          this.isLoading = false
        })
        .catch((error) => {
          this.isLoading = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  },
  mounted () {
    this.getTransactionDetails()
    if (
      this.transactionDetails.status === this.$STATUS.AWAITING_BUSINESS_APPROVAL
    ) {
      this.getInitiatorDetails()
    }
    if (this.statusType === this.$STATUS.ONGOING) {
      this.activeComponent = 'first-page'
    } else {
      this.activeComponent = 'history-page'
    }
  }
}
</script>
<style scoped>
>>> .el-drawer__header > :first-child {
  font-size: 0.9rem;
  color: var(--text-2);
  font-weight: 600;
}
>>> .el-drawer__header {
  color: var(--text-4);
  font-size: 1.2rem;
  padding-bottom: 1rem;
  padding-top: 2rem;
  padding-left: 1.4rem;
  font-weight: 700;
  border-bottom: 1px solid var(--border-2);
}
>>> .el-drawer {
  background: var(--background-9) !important;
}
>>> .el-input--prefix .el-input__inner {
  border: 1px solid var(--border-1);
  border-radius: 8px;
  margin: 0, 6px;
  font-size: 0.9rem;
  color: var(--text-2);
  font-weight: 400;
  padding-left: 40px;
  background: var(--background-10);
}
.small-text {
  font-size: 0.6rem;
}

>>> .el-pagination.is-background .btn-next,
.el-pagination.is-background .btn-prev,
.el-pagination.is-background .el-pager li {
  background: var(--background-2) !important;
}
>>> li.number.active {
  background-color: var(--background) !important;
  border: 0.5px solid var(--color-primary) !important;
  box-sizing: border-box;
  border-radius: 100px;
  font-weight: 400;
  color: var(--color-primary) !important ;
}
>>> li.number {
  background-color: var(--background) !important;
  border: 0.5px solid var(--text-1) !important;
  box-sizing: border-box;
  border-radius: 100px !important;
  font-weight: 400;
}
.header {
}
</style>
